import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRateLimit, loginRateLimiter } from '../../utils/rateLimiting';

const Login = () => {
  const [pid, setPid] = useState('');
  const [studentName, setStudentName] = useState('');
  const [needsName, setNeedsName] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminMode, setAdminMode] = useState('login'); // 'login' | 'signup' | 'forgot'
  const { login, signup, loginAsGuest, resetPassword, currentUser } = useAuth();
  const navigate = useNavigate();
  const { isAllowed: isLoginAllowed, getResetTime } = useRateLimit(loginRateLimiter);

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const handleStudentLogin = async (e) => {
    e.preventDefault();

    if (!pid.trim()) {
      return setError('Please enter your Student ID');
    }

    // If we're in the name prompt step, validate name
    if (needsName) {
      if (!studentName.trim()) {
        return setError('Please enter your name');
      }
    }

    if (!isLoginAllowed()) {
      const resetTime = new Date(getResetTime());
      return setError(`Too many login attempts. Please try again after ${resetTime.toLocaleTimeString()}`);
    }

    try {
      setError('');
      setLoading(true);
      await loginAsGuest(pid.trim(), needsName ? studentName.trim() : undefined);
      navigate('/dashboard');
    } catch (error) {
      if (error.message === 'STUDENT_NOT_FOUND') {
        // PID not recognized -- ask for name to register
        setNeedsName(true);
        setError('');
      } else {
        setError(error.message || 'Failed to sign in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePidChange = (e) => {
    setPid(e.target.value);
    // Reset the name prompt if user changes their PID
    if (needsName) {
      setNeedsName(false);
      setStudentName('');
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      return setError('Please fill in all fields');
    }

    if (!isLoginAllowed()) {
      const resetTime = new Date(getResetTime());
      return setError(`Too many login attempts. Please try again after ${resetTime.toLocaleTimeString()}`);
    }

    try {
      setError('');
      setAdminLoading(true);
      await login(email, password);
      navigate('/dashboard');
    } catch (error) {
      setError('Failed to sign in: ' + (error.message || 'Unknown error'));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleAdminSignup = async (e) => {
    e.preventDefault();

    if (!email || !password || !signupName.trim()) {
      return setError('Please fill in all fields');
    }

    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    try {
      setError('');
      setAdminLoading(true);
      await signup(email, password, signupName.trim());
      navigate('/dashboard');
    } catch (error) {
      setError('Failed to create account: ' + (error.message || 'Unknown error'));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email) {
      return setError('Please enter your email address');
    }

    try {
      setError('');
      setAdminLoading(true);
      await resetPassword(email);
      setSuccessMessage('Password reset email sent. Check your inbox.');
    } catch (error) {
      setError('Failed to send reset email: ' + (error.message || 'Unknown error'));
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900">Sign In</h1>
          <p className="mt-2 text-sm text-gray-600">
            MPCL Inventory System
          </p>
        </div>

        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-100 border border-red-200 rounded-md">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="p-3 text-sm text-green-600 bg-green-100 border border-green-200 rounded-md">
            {successMessage}
          </div>
        )}

        {/* Student ID Login - Primary */}
        <form className="mt-8 space-y-4" onSubmit={handleStudentLogin}>
          <div>
            <label htmlFor="pid" className="block text-sm font-medium text-gray-700">
              Student ID
            </label>
            <input
              id="pid"
              name="pid"
              type="text"
              required
              value={pid}
              onChange={handlePidChange}
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your Student ID"
            />
          </div>

          {needsName && (
            <div>
              <label htmlFor="studentName" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <p className="text-xs text-gray-500 mt-1">
                This ID is not yet registered. Please enter your name to continue.
              </p>
              <input
                id="studentName"
                name="studentName"
                type="text"
                required
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your full name"
                autoFocus
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex justify-center w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : needsName ? 'Register & Sign In' : 'Sign In'}
          </button>
        </form>

        {/* Admin Login - Secondary */}
        <div className="pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => { setShowAdminLogin(!showAdminLogin); setError(''); setSuccessMessage(''); setAdminMode('login'); }}
            className="flex items-center justify-center w-full text-sm text-gray-500 hover:text-gray-700"
          >
            {showAdminLogin ? 'Hide Admin Login' : 'Admin Login'}
            <svg
              className={`ml-1 w-4 h-4 transition-transform ${showAdminLogin ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAdminLogin && (
            <div className="mt-4">
              {adminMode === 'login' && (
                <form className="space-y-4" onSubmit={handleAdminLogin}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="admin@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={adminLoading}
                    className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-gray-700 border border-transparent rounded-md shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {adminLoading ? 'Signing in...' : 'Sign in as Admin'}
                  </button>

                  <div className="flex justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => { setError(''); setSuccessMessage(''); setAdminMode('signup'); }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Create Account
                    </button>
                    <button
                      type="button"
                      onClick={() => { setError(''); setSuccessMessage(''); setAdminMode('forgot'); }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </form>
              )}

              {adminMode === 'signup' && (
                <form className="space-y-4" onSubmit={handleAdminSignup}>
                  <p className="text-xs text-gray-500">
                    Only whitelisted email addresses can create an admin account.
                  </p>
                  <div>
                    <label htmlFor="signupName" className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      id="signupName"
                      name="signupName"
                      type="text"
                      required
                      value={signupName}
                      onChange={(e) => setSignupName(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="signupEmail" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="signupEmail"
                      name="signupEmail"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="admin@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="signupPassword" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      id="signupPassword"
                      name="signupPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="At least 6 characters"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={adminLoading}
                    className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-gray-700 border border-transparent rounded-md shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {adminLoading ? 'Creating account...' : 'Create Admin Account'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setError(''); setSuccessMessage(''); setAdminMode('login'); }}
                    className="w-full text-xs text-blue-600 hover:text-blue-800"
                  >
                    Back to Login
                  </button>
                </form>
              )}

              {adminMode === 'forgot' && (
                <form className="space-y-4" onSubmit={handleForgotPassword}>
                  <p className="text-xs text-gray-500">
                    Enter your email address and we'll send you a password reset link.
                  </p>
                  <div>
                    <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="resetEmail"
                      name="resetEmail"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="admin@email.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={adminLoading}
                    className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-gray-700 border border-transparent rounded-md shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {adminLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setError(''); setSuccessMessage(''); setAdminMode('login'); }}
                    className="w-full text-xs text-blue-600 hover:text-blue-800"
                  >
                    Back to Login
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
